import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { CalendarSession } from '@/features/institute/calendar/types/calendar';
import type { InstituteNoticeList } from '@/types/instituteNotice';
import type {
  MyFeeAccount,
  StudentActivityList,
  StudentHomeworkList,
  StudentLectureList,
  StudentMarksList,
  StudentNoticeList,
  StudentOverview,
  StudentProfile,
  StudentProfileRecord,
  StudentTimetable,
} from '@/types/student';

/** What comes back from a hand-in, whichever of the two forms it took. */
interface HandedIn {
  handedInOn: string;
  attachmentName: string | null;
  attachmentSize: number | null;
  repoUrl: string | null;
}

/** What comes back from handing an activity in. */
interface ActivityHandedIn {
  handedInOn: string;
  fileName: string | null;
  fileSize: number | null;
}

/**
 * Every call the student panel makes.
 *
 * Its own area rather than a corner of the institute's: a student is not staff,
 * and none of `/institute/*` is open to their role. No path here carries a
 * student id — the server resolves the child from the caller's token, so there
 * is nothing for one of them to point at another's record.
 */
export const studentApi = {
  /** The child's own record — the header, the sidebar and every tab's title. */
  me: () => unwrap(apiClient.get<ApiSuccess<StudentProfile>>('/student/me')),

  /** Private, like every upload — needs the access token, so use SecureImage. */
  photoPath: () => '/student/photo',

  /**
   * Their own fee account and every receipt against it.
   *
   * No id and no filters — the ledger is the caller's own, resolved from their
   * token, so there is nothing here to point at another family's account.
   */
  fees: () => unwrap(apiClient.get<ApiSuccess<MyFeeAccount>>('/student/fees')),

  /**
   * The whole record behind the profile screen.
   *
   * Its own call rather than more fields on `me()`: that one is read on every
   * page of the panel, and this carries the home address and the family's
   * numbers.
   */
  profile: () => unwrap(apiClient.get<ApiSuccess<StudentProfileRecord>>('/student/profile')),

  /** Every card on the overview tab, answered in one request. */
  overview: () => unwrap(apiClient.get<ApiSuccess<StudentOverview>>('/student/overview')),

  /**
   * One week of their section's timetable, with any papers sat in it. `weekOf`
   * is any day in the week; today's week when omitted.
   */
  timetable: (weekOf?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StudentTimetable>>('/student/timetable', {
        params: weekOf ? { weekOf } : undefined,
      }),
    ),

  /** The work set for their section, with their own submission against each. */
  homework: () => unwrap(apiClient.get<ApiSuccess<StudentHomeworkList>>('/student/homework')),

  /**
   * Where the worksheet their teacher attached is fetched from.
   *
   * `worksheet`, not `attachment`: the path below is their *own* handed-in file,
   * and one homework uuid has both hanging off it.
   */
  worksheetPath: (homeworkUuid: string) => `/student/homework/${homeworkUuid}/worksheet`,

  /**
   * Hands one piece of work in as a file.
   *
   * Multipart, with the file in the one slot the server accepts. Handing in
   * again replaces what was there — until a teacher marks it, after which the
   * server refuses rather than letting a mark end up about a file nobody can
   * see any more.
   */
  submitHomework: (homeworkUuid: string, file: File, notes?: string) => {
    const body = new FormData();
    body.append('solution', file);
    if (notes) body.append('notes', notes);

    // The client's JSON default makes axios serialise the form to JSON, which
    // drops the file. Unset so the browser adds the multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<HandedIn>>(`/student/homework/${homeworkUuid}/submit`, body, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  /**
   * Hands one in as the address of a repository, for work set that way.
   *
   * The same endpoint as the file above, sent as JSON instead of multipart —
   * one hand-in, one route, and the server refuses whichever of the two the
   * work did not ask for.
   */
  submitHomeworkRepo: (homeworkUuid: string, repoUrl: string) =>
    unwrap(
      apiClient.post<ApiSuccess<HandedIn>>(`/student/homework/${homeworkUuid}/submit`, {
        repoUrl,
      }),
    ),

  /** Where their own submitted file is fetched from — private, like every upload. */
  submissionFilePath: (homeworkUuid: string) => `/student/homework/${homeworkUuid}/attachment`,

  /** The class activities set for their section — projects, practicals, presentations. */
  activities: () => unwrap(apiClient.get<ApiSuccess<StudentActivityList>>('/student/activities')),

  /** Where the guidelines sheet their teacher attached to an activity is fetched from. */
  activityGuidelinePath: (activityUuid: string) => `/student/activities/${activityUuid}/guidelines`,

  /**
   * Hands one activity in: a file, and an optional note to the teacher.
   * Handing in again replaces what was there until the teacher scores it.
   */
  submitActivity: (activityUuid: string, file: File, notes?: string) => {
    const body = new FormData();
    body.append('solution', file);
    if (notes) body.append('notes', notes);

    // As for homework: unset the JSON default so the browser writes the
    // multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<ActivityHandedIn>>(`/student/activities/${activityUuid}/submit`, body, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  /** Where their own handed-in file for an activity is fetched from. */
  activitySubmissionFilePath: (activityUuid: string) => `/student/activities/${activityUuid}/attachment`,

  /**
   * The notices their class has been given.
   *
   * Read-only: a child is told things here and has nothing to send back, which
   * is why there is no counterpart to the teacher's post and withdraw.
   */
  notices: () => unwrap(apiClient.get<ApiSuccess<StudentNoticeList>>('/student/notices')),

  /**
   * The institute's own board, as far as it is addressed to the children.
   *
   * A separate call from `notices` above because it is a separate board: that
   * one is their class teacher's, resolved from their section, and this one is
   * the office's, resolved from its audience. Neither carries an id.
   */
  noticeBoard: () =>
    unwrap(apiClient.get<ApiSuccess<InstituteNoticeList>>('/student/notice-board')),

  /**
   * This session's academic calendar, narrowed by the server to what was
   * addressed to everyone or to students, and to their class's band.
   */
  calendar: () => unwrap(apiClient.get<ApiSuccess<CalendarSession>>('/student/calendar')),

  /** Every paper they have been marked on this session. */
  marks: () => unwrap(apiClient.get<ApiSuccess<StudentMarksList>>('/student/marks')),

  /** The lessons posted to their section, and which they have watched. */
  lectures: () => unwrap(apiClient.get<ApiSuccess<StudentLectureList>>('/student/lectures')),

  /**
   * "I have watched this."
   *
   * Idempotent on the server, so pressing play twice leaves one row and the
   * teacher's figure stays a headcount rather than a play count.
   */
  markLectureWatched: (recordingUuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<{ watched: boolean }>>(`/student/lectures/${recordingUuid}/watched`),
    ),

  /**
   * Bookmarking a lesson, and taking the bookmark off again.
   *
   * A different column from the watch, so shortlisting a lesson they have not
   * opened does not tell their teacher they watched it.
   */
  setLectureSaved: (recordingUuid: string, saved: boolean) =>
    unwrap(
      saved
        ? apiClient.post<ApiSuccess<{ saved: boolean }>>(`/student/lectures/${recordingUuid}/save`)
        : apiClient.delete<ApiSuccess<{ saved: boolean }>>(
            `/student/lectures/${recordingUuid}/save`,
          ),
    ),
};
